import React from "react";
import { ChildChip } from "./SubprocessProjectionBits";
import {
  normalizedChildren,
  parentRollupUnavailableReason,
  getFallbackChildren,
} from "../../utils/subprocessRedesign";

// Per-child results for an engine that reports what it did INSIDE each sub-process
// but cannot report a combined parent figure. That is the greedy engine's permanent
// position (see parentRollupUnavailableReason) — its numbers come from the CMS
// what-if, which has no way to express a redesigned sub-process.
//
// The whole point of this component is that every figure it shows describes ONE
// sub-process, never the parent and never a share of it. The backend says the same
// thing in its own `note`; the layout has to hold the line too, which is why there
// is no total row anywhere here.

function pct(v) {
  if (v == null) return "—";
  const improved = v > 0;
  const color = v === 0 ? "#6b7280" : improved ? "#059669" : "#dc2626";
  const glyph = v === 0 ? "–" : improved ? "▼" : "▲";
  return <span style={{ fontWeight: 800, color }}>{glyph} {Math.abs(v).toFixed(1)}%</span>;
}

/**
 * The CMS's stored flow analysis and its what-if disagree on this child before any
 * redesign. Shown wherever that child's figures are, because the AS-IS here will not
 * match the AS-IS the same sub-process shows on its own Overview tab, and without
 * this the difference reads as an unexplained inconsistency in the app.
 */
function MeasurementGapNote({ gap, storedAsIs, fmtMin, money }) {
  if (!gap) return null;
  const worst = Math.max(Math.abs(gap.cycle_time_gap_pct || 0), Math.abs(gap.labor_cost_gap_pct || 0));
  return (
    <div style={{ marginTop: 6, padding: "6px 10px", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, fontSize: 10.5, color: "#92400e" }}>
      <strong>Baseline differs from this sub-process&apos;s stored figure by up to {worst.toFixed(0)}%.</strong>{" "}
      {storedAsIs && (
        <>Stored: {fmtMin(storedAsIs.cycle_time_minutes)} · {money(storedAsIs.labor_cost_per_case)}. </>
      )}
      The reduction above is measured what-if to what-if, so it excludes this gap entirely —
      the gap is a measurement difference between two CMS endpoints, not a saving.
    </div>
  );
}

// `applied: false` is a real, useful answer here — usually "nothing in this
// sub-process is classified yet", which is an action the analyst can take. It is not
// an error and must not render as one.
function NvaNote({ nva }) {
  if (!nva || nva.checked !== true) return null;
  const applied = nva.applied === true;
  return (
    <div style={{ marginTop: 6, fontSize: 10.5, color: applied ? "#075985" : "#9ca3af" }}>
      {applied ? (
        <>
          <strong>{(nva.eliminated || []).length} non-value-adding step(s) removed:</strong>{" "}
          {(nva.eliminated || []).map((t) => t.task_name || `Task ${t.task_id}`).join(", ")}
        </>
      ) : (
        nva.note
      )}
    </div>
  );
}

function ChildCard({ child, fmtMin, money }) {
  return (
    <div style={{ flex: "1 1 320px", minWidth: "min(300px, 100%)", border: "1px solid #eef2f7", borderRadius: 10, padding: "10px 14px", background: "#fff" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 6 }}>
        <ChildChip name={child.name} />
        {child.appliedHeuristics.length === 0 && (
          <span style={{ fontSize: 10.5, color: "#9ca3af" }}>no heuristic qualified</span>
        )}
      </div>

      <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 11.5 }}>
        <thead>
          <tr>
            {["", "AS-IS", "TO-BE", "Δ"].map((h, i) => (
              <th key={h || "l"} style={{ padding: "3px 6px", fontSize: 9.5, color: "#6b7280", textAlign: i === 0 ? "left" : "right", textTransform: "uppercase", borderBottom: "1px solid #eef2f7" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={{ padding: "3px 6px", fontWeight: 600 }}>Cycle time</td>
            <td style={{ padding: "3px 6px", textAlign: "right" }}>{fmtMin(child.asIsCycle)}</td>
            <td style={{ padding: "3px 6px", textAlign: "right", fontWeight: 700 }}>{fmtMin(child.toBeCycle)}</td>
            <td style={{ padding: "3px 6px", textAlign: "right" }}>{pct(child.cycleReductionPct)}</td>
          </tr>
          <tr>
            <td style={{ padding: "3px 6px", fontWeight: 600 }}>Labor cost</td>
            <td style={{ padding: "3px 6px", textAlign: "right" }}>{money(child.asIsCost)}</td>
            <td style={{ padding: "3px 6px", textAlign: "right", fontWeight: 700 }}>{money(child.toBeCost)}</td>
            <td style={{ padding: "3px 6px", textAlign: "right" }}>{pct(child.costReductionPct)}</td>
          </tr>
        </tbody>
      </table>

      {child.appliedHeuristics.length > 0 && (
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 7 }}>
          {child.appliedHeuristics.map((h) => (
            <span key={h} style={{ fontSize: 9.5, fontWeight: 700, color: "#15803d", background: "#dcfce7", border: "1px solid #86efac", borderRadius: 999, padding: "1px 7px" }}>
              ✓ {String(h).replace(/[_-]+/g, " ")}
            </span>
          ))}
        </div>
      )}

      <NvaNote nva={child.nvaElimination} />
      <MeasurementGapNote gap={child.measurementGap} storedAsIs={child.storedAsIs} fmtMin={fmtMin} money={money} />
    </div>
  );
}

export default function SubprocessChildResults({ projection, fmtMin, money, title }) {
  if (!projection) return null;

  const children = normalizedChildren(projection);
  const fallbacks = getFallbackChildren(projection);
  const rollupBlocked = parentRollupUnavailableReason(projection);

  if (children.length === 0 && fallbacks.length === 0) return null;

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", marginBottom: 6 }}>
        {title || "SUB-PROCESS RESULTS — each measured on its own"}
      </div>

      {/* Without this the reader adds the child percentages up, or reads the biggest
          one as the parent's gain. The backend refuses to publish a combined figure
          for exactly that reason, so the UI has to say why one is missing rather
          than leave a conspicuous hole. */}
      {rollupBlocked && (
        <div style={{ marginBottom: 10, padding: "8px 12px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, fontSize: 11.5, color: "#475569" }}>
          <strong>These do not roll up into this process&apos;s own numbers.</strong> Each percentage
          below describes that sub-process alone, never its share of the parent, and the KPIs on the
          other tabs still describe this process with every sub-process as stored.
          <div style={{ marginTop: 4, color: "#9ca3af" }}>{rollupBlocked}</div>
        </div>
      )}

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        {children.map((c) => (
          <ChildCard key={c.id ?? c.name} child={c} fmtMin={fmtMin} money={money} />
        ))}
      </div>

      {fallbacks.length > 0 && (
        <div style={{ marginTop: 10, padding: "8px 12px", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, fontSize: 11.5, color: "#92400e" }}>
          <strong>{fallbacks.length} sub-process{fallbacks.length === 1 ? "" : "es"} could not be redesigned:</strong>
          <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
            {fallbacks.map((c, i) => (
              <li key={c.child_process_id ?? i}>
                <strong>{c.child_process_name || `Process ${c.child_process_id}`}</strong>
                {c.reason ? ` — ${c.reason}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
